"use client";

import {
  createLDReactProvider,
  LDReactContext,
  useInitializationStatus,
  useLDClient,
  type LDContext,
  type LDReactClient,
  type LDReactClientContextValue,
} from "@launchdarkly/react-sdk";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { FeatureFlagsBridge } from "./FeatureFlags";
import { Spinner } from "@clarityos/ui";

const LD_INIT_TIMEOUT_SECONDS = 5;

type LaunchDarklyProviderProps = {
  children: ReactNode;
};

type LDProviderComponent = ReturnType<typeof createLDReactProvider>;

// Keep one SDK client and stream per browser tab. Authenticated identity changes
// are applied to this client by a separate bridge instead of recreating it.
let cachedLDProvider: LDProviderComponent | undefined;

const noOpLDClient = {
  isReady: () => false,
  off: () => undefined,
  on: () => undefined,
} as unknown as LDReactClient;

const noOpLDContextValue: LDReactClientContextValue = {
  client: noOpLDClient,
  initializedState: "initializing",
};

function NoOpLaunchDarklyProvider({ children }: LaunchDarklyProviderProps) {
  return (
    <LDReactContext.Provider value={noOpLDContextValue}>
      {children}
    </LDReactContext.Provider>
  );
}

function getEnvironment(): string {
  return process.env.NEXT_PUBLIC_ENVIRONMENT ?? "development";
}

function getLaunchDarklyClientSideID(): string | undefined {
  const value = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID?.trim();
  return value || undefined;
}

export function buildAnonymousLaunchDarklyContext(): LDContext {
  return {
    kind: "user",
    anonymous: true,
    // Omitting `key` lets the browser SDK generate and persist one.
    custom: { environment: getEnvironment() },
  };
}

function resolveLDProvider(): LDProviderComponent {
  if (!cachedLDProvider) {
    const clientSideID = getLaunchDarklyClientSideID();

    if (!clientSideID) {
      throw new Error(
        "Cannot create the LaunchDarkly client without a client-side ID.",
      );
    }

    cachedLDProvider = createLDReactProvider(
      clientSideID,
      buildAnonymousLaunchDarklyContext(),
      {
        deferInitialization: true,
        // CCOF does not use LaunchDarkly Experimentation goals.
        ldOptions: { fetchGoals: false },
        startOptions: { timeout: LD_INIT_TIMEOUT_SECONDS },
      },
    );
  }

  return cachedLDProvider;
}

export function __resetLaunchDarklyProviderForTests(): void {
  cachedLDProvider = undefined;
}

function isE2EMode(): boolean {
  return typeof window !== "undefined" && window.__E2E_TEST_MODE__ === true;
}

const subscribeNoop = () => () => {};

function useIsHydrated(): boolean {
  // Keep the server and initial browser render aligned before consulting E2E state.
  return useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );
}

function LaunchDarklyStarter({
  enabled,
  onStartFailure,
}: {
  enabled: boolean;
  onStartFailure: (error: unknown) => void;
}) {
  const ldClient = useLDClient();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current || !ldClient) {
      return;
    }

    startedRef.current = true;

    if (isE2EMode() || !getLaunchDarklyClientSideID()) {
      return;
    }

    void ldClient
      .start({ timeout: LD_INIT_TIMEOUT_SECONDS })
      .catch(onStartFailure);
  }, [enabled, ldClient, onStartFailure]);

  return null;
}

function LaunchDarklyGate({
  children,
  startFailed,
}: LaunchDarklyProviderProps & { startFailed: boolean }) {
  const initialization = useInitializationStatus();
  const isHydrated = useIsHydrated();
  const hasClientSideID = Boolean(getLaunchDarklyClientSideID());

  if (!hasClientSideID) {
    return <>{children}</>;
  }

  if (!isHydrated) {
    return <Spinner alignment="fixedCentered" />;
  }

  if (isE2EMode() || startFailed) {
    return <>{children}</>;
  }

  if (initialization.status === "initializing") {
    return <Spinner alignment="fixedCentered" />;
  }

  if (
    initialization.status === "failed" ||
    initialization.status === "timeout"
  ) {
    return <>{children}</>;
  }

  return <>{children}</>;
}

function LaunchDarklyRuntime({
  children,
  enabled,
  providerCreationFailed,
}: LaunchDarklyProviderProps & {
  enabled: boolean;
  providerCreationFailed: boolean;
}) {
  const [startFailed, setStartFailed] = useState(false);
  const handleStartFailure = useCallback((error: unknown) => {
    console.warn(
      "LaunchDarkly failed to start; rendering without feature flags",
      error,
    );
    setStartFailed(true);
  }, []);

  return (
    <>
      <LaunchDarklyStarter
        enabled={enabled}
        onStartFailure={handleStartFailure}
      />
      <LaunchDarklyGate startFailed={startFailed || providerCreationFailed}>
        {children}
      </LaunchDarklyGate>
    </>
  );
}

export function LaunchDarklyProvider({ children }: LaunchDarklyProviderProps) {
  const [Provider, setProvider] = useState<LDProviderComponent>();
  const [providerCreationFailed, setProviderCreationFailed] = useState(false);

  useEffect(() => {
    if (isE2EMode() || !getLaunchDarklyClientSideID()) {
      return;
    }

    try {
      const resolvedProvider = resolveLDProvider();
      setProvider(() => resolvedProvider);
    } catch (error) {
      console.warn(
        "LaunchDarkly client creation failed; rendering without feature flags",
        error,
      );
      setProviderCreationFailed(true);
    }
  }, []);

  const RuntimeProvider = Provider ?? NoOpLaunchDarklyProvider;

  // SSR, initial hydration, missing-config, and E2E renders use a local no-op
  // context. The real browser client is only constructed after hydration so
  // builds stay side-effect-free while hooks always receive a valid context.
  return (
    <RuntimeProvider>
      <LaunchDarklyRuntime
        enabled={Provider !== undefined}
        providerCreationFailed={providerCreationFailed}
      >
        <FeatureFlagsBridge>{children}</FeatureFlagsBridge>
      </LaunchDarklyRuntime>
    </RuntimeProvider>
  );
}
